import { useForm } from "antd/lib/form/Form";
import { useDispatch, useSelector } from "react-redux";
import { type CSSProperties, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Drawer, Row, Space } from "antd";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";
import { BiFilterAlt } from "react-icons/bi";
import { CloseOutlined, EditOutlined, EyeOutlined, FlagOutlined,DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import { getMilestonesList } from "../../core/store/reducers/milestone.slice";
import { RootState } from "../../shared/types/common";
import { formatCost1, showNotification,accessAllowed } from "../../shared/utils/utils";
import { Client } from "../../core/api-client";
import { allPermissions } from "../../shared/constants/constants";
import {  
  API_GET_ALL_PROJECT_LIST_MILESTONES,
  API_GET_ALL_STAFF_LIST,  
  API_MILESTONES_LIST,
} from "../../shared/routes/private.routes";
import Papa from "papaparse";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import CreateEditMilestoneComponent from "./components/create-edit-milestone.component";

const INVOICE_STATUS_OPTIONS = [
  { value: "NOT DUE", label: "NOT DUE" },
  { value: "RAISED", label: "RAISED" },
  { value: "PAID", label: "PAID" },
];

const MILESTONE_FILTER_LABELS: Record<string, string> = {
  name: "Client name",
  projectId: "Project",
  poNumber: "PO#",
  spoc: "SPOC",
  invoiceStatus: "Invoice status",
  paymentReceived: "Payment received",
};

function milestoneFilterValueText(
  key: string,
  value: any,
  projectOptions: { value: string; label: string }[],
  staffOptions: { value: string; label: string }[]
): string {
  switch (key) {
    case "name":
    case "poNumber":
      return String(value).trim();
    case "projectId":
      return (
        projectOptions.find((o) => o.value === value)?.label ?? String(value)
      );
    case "spoc":
      return staffOptions.find((o) => o.value === value)?.label ?? String(value);
    case "invoiceStatus":
      return String(value);
    case "paymentReceived":
      return value === true ? "Yes" : "No";
    default:
      return String(value);
  }
}

const MILESTONE_FILTER_CLEAR: Record<string, any> = {
  name: undefined,
  projectId: undefined,
  poNumber: undefined,
  spoc: undefined,
  invoiceStatus: undefined,
  paymentReceived: undefined,
};

const MILESTONE_PAGE_SIZE = 15;
const MILESTONE_TABLE_NOWRAP_STYLE: CSSProperties = {
  whiteSpace: "nowrap",
  wordBreak: "keep-all",
  width: "max-content",
  minWidth: "max-content",
};
const MILESTONE_TABLE_NOWRAP_CONTENT_STYLE: CSSProperties = {
  ...MILESTONE_TABLE_NOWRAP_STYLE,
  display: "inline-block",
};

const withMilestoneNoWrapColumns = (columns: any[]) =>
  columns.map((column) => {
    const skipContentWrap = column.key === "actions";
    return {
      ...column,
      ellipsis: skipContentWrap ? column.ellipsis : false,
      width: skipContentWrap ? column.width : undefined,
      title:
        !skipContentWrap && typeof column.title === "string" ? (
          <span style={MILESTONE_TABLE_NOWRAP_CONTENT_STYLE}>{column.title}</span>
        ) : (
          column.title
        ),
      render: skipContentWrap
        ? column.render
        : (...args: any[]) => (
            <span style={MILESTONE_TABLE_NOWRAP_CONTENT_STYLE}>
              {column.render ? column.render(...args) : args[0]}
            </span>
          ),
      onCell: (...args: any[]) => {
        const cellProps = column.onCell?.(...args) || {};
        return {
          ...cellProps,
          style: {
            ...cellProps.style,
            ...MILESTONE_TABLE_NOWRAP_STYLE,
          },
        };
      },
      onHeaderCell: (...args: any[]) => {
        const headerProps = column.onHeaderCell?.(...args) || {};
        return {
          ...headerProps,
          style: {
            ...headerProps.style,
            ...MILESTONE_TABLE_NOWRAP_STYLE,
          },
        };
      },
    };
  });

const ManageMilestonesPage: React.FC = () => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const {
    loadingMilestonesList,
    milestonesList,
    pagination,
  } = useSelector((state: RootState) => state.milestone);

  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState<any>({});
  const [sorter, setSorter] = useState({ sort_by: "order", sort_type: "ASC" });
  const [listData, setListData] = useState<any[]>([]);
  const [staffOptions, setStaffOptions] = useState<{ value: string; label: string }[]>([]);
  const [projectOptions, setProjectOptions] = useState<{ value: string; label: string }[]>([]);
  const [activityLogVisible, setActivityLogVisible] = useState(false);
  const [selectedMilestoneForLog, setSelectedMilestoneForLog] = useState<any>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<any>(null);
  const [pageSize, setPageSize] = useState(MILESTONE_PAGE_SIZE);
  const { myPermissions } = useSelector((state: RootState) => state.auth);


  const loadList = useCallback(
    (page: number | null = null) => {
      const body: any = {
        page: page ?? pagination?.page ?? 1,
        limit: pageSize,
        sort_by: sorter.sort_by || "order",
        sort_type: sorter.sort_type || "ASC",
      };
      Object.keys(filters).forEach((key) => {
        if (filters[key] !== undefined && filters[key] !== "" && filters[key] !== null) {
          body[key] = filters[key];
        }
      });
      dispatch(getMilestonesList({ body }));
    },
    [dispatch, filters, pageSize, pagination?.page, sorter]
  );

  useEffect(() => {
    loadList(1);
  }, [filters, sorter, pageSize]);

  useEffect(() => {
    if (milestonesList?.length) {
      setListData(milestonesList.map((r: any, i: number) => ({ key: i, ...r })));
    } else {
      setListData([]);
    }
  }, [milestonesList]);

  useEffect(() => {
    Client.get(API_GET_ALL_STAFF_LIST).then((res: any) => {
      const list = res?.data?.data || [];
      setStaffOptions(
        list.map((s: any) => ({
          value: s.staffId,
          label: s.staffName || s.employeeId || s.staffId,
        }))
      );
    });
    Client.get(API_GET_ALL_PROJECT_LIST_MILESTONES).then((res: any) => {
      const list = res?.data?.data?.data || res?.data?.data || [];
      setProjectOptions(
        list.map((p: any) => ({
          value: p.projectId,
          label: p?.l2ProjectName + " (" + p?.sowNumber + ")",
        }))
      );
    });
  }, []);

  const onFilterApply = (values: any) => {
    setFilters(values);
  };

  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((k) => isFilterValueActive(filters[k]))
      .map((key) => ({
        key,
        fieldLabel: MILESTONE_FILTER_LABELS[key] || key,
        valueText: milestoneFilterValueText(
          key,
          filters[key],
          projectOptions,
          staffOptions
        ),
      }));
  }, [filters, projectOptions, staffOptions]);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    form.resetFields();
  }, [form]);

  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const cleared =
        key in MILESTONE_FILTER_CLEAR ? MILESTONE_FILTER_CLEAR[key] : undefined;
      form.setFieldsValue({ [key]: cleared });
    },
    [form]
  );

  const onPageChange = (pageNumber: number) => {
    loadList(pageNumber);
  };
  const onPageSizeChange = (_pageNumber: number, selectedPageSize: number) => {
    setPageSize(selectedPageSize);
  };
  const onChangeHandler = (_pagination: any, _filters: any, tableSorter: any) => {
    const activeSorter = Array.isArray(tableSorter)
      ? tableSorter.find((item) => item?.order)
      : tableSorter;

    if (!activeSorter?.order) {
      setSorter({ sort_by: "order", sort_type: "ASC" });
      return;
    }

    setSorter({
      sort_by: activeSorter.columnKey || "order",
      sort_type: activeSorter.order === "ascend" ? "ASC" : "DESC",
    });
  };

  const getSortOrder = (columnKey: string) => {
    if (sorter.sort_by !== columnKey) return null;
    return sorter.sort_type === "ASC" ? "ascend" : "descend";
  };

  const handleViewActivityLog = (record: any) => {
    setSelectedMilestoneForLog(record);
    setActivityLogVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingMilestone(record);
    setEditModalVisible(true);
  };

  const handleEditSuccess = () => {
    loadList(pagination?.page ?? 1);
    setEditModalVisible(false);
    setEditingMilestone(null);
  };

  const getSortableColumnProps = (columnKey: string) => ({
    sorter: true,
    sortOrder: getSortOrder(columnKey),
  });

  const columns = useMemo(
    () => [
      {
        title: "Client",
        dataIndex: "accountName",
        key: "accountName",
        ...getSortableColumnProps("accountName"),
        width: 90,
        ellipsis: true,
        render: (v: any) => v ?? "—",
      },
      { title: "SoW#", dataIndex: "sowNumber", key: "sowNumber", ...getSortableColumnProps("sowNumber"), width: 140, ellipsis: true },
      { title: "PO Number", dataIndex: "poNumber", key: "poNumber", ...getSortableColumnProps("poNumber"), width: 140, ellipsis: true },
      { title: "Project", dataIndex: "projectName", key: "projectName", ...getSortableColumnProps("projectName"), width: 160, ellipsis: true },
      { title: "SPOC", dataIndex: "spocName", key: "spocName", ...getSortableColumnProps("spocName"), width: 120, ellipsis: true },
      { title: "Milestone", dataIndex: "name", key: "name", ...getSortableColumnProps("name"), width: 160, ellipsis: true },
      {
        title: "%Age",
        dataIndex: "percentage",
        key: "percentage",
        ...getSortableColumnProps("percentage"),
        width: 70,
        render: (v: any) => (v != null ? Number(v) : "-"),
      },
      {
        title: "PO Currency",
        dataIndex: "poCurrencyName",
        key: "poCurrencyName",
        ...getSortableColumnProps("poCurrencyName"),
        width: 100,
        render: (v: any) => v ?? "-",
      },
      {
        title: "PO Value",
        dataIndex: "poValue",
        key: "poValue",
        ...getSortableColumnProps("poValue"),
        width: 110,
        render: (v: any, record: any) =>
          v != null ? formatCost1(Number(v), record.poCurrencyName || "") : "-",
      },
      {
        title: "Milestone Value",
        key: "milestoneValue",
        ...getSortableColumnProps("milestoneValue"),
        width: 120,
        render: (_: any, record: any) => {
          const pct = record.percentage != null ? Number(record.percentage) : null;
          const poVal = record.poValue != null ? Number(record.poValue) : null;
          if (pct != null && poVal != null) {
            const val = (poVal * pct) / 100;
            return formatCost1(val, record.poCurrencyName || "");
          }
          return "-";
        },
      },
      {
        title: "Est Billing Date",
        dataIndex: "estimatedBillingDate",
        key: "estimatedBillingDate",
        ...getSortableColumnProps("estimatedBillingDate"),
        width: 120,
        render: (v: string) => (v ? moment(v).format("DD-MMM-YYYY") : "-"),
      },
      {
        title: "Invoice Status",
        dataIndex: "invoiceStatus",
        key: "invoiceStatus",
        ...getSortableColumnProps("invoiceStatus"),
        width: 110,
      },
      {
        title: "Invoice Date",
        dataIndex: "invoiceDate",
        key: "invoiceDate",
        ...getSortableColumnProps("invoiceDate"),
        width: 110,
        render: (v: string) => (v ? moment(v).format("DD-MMM-YYYY") : "-"),
      },
      {
        title: "Due Date",
        dataIndex: "invoiceDueDate",
        key: "invoiceDueDate",
        ...getSortableColumnProps("invoiceDueDate"),
        width: 110,
        render: (v: string) => (v ? moment(v).format("DD-MMM-YYYY") : "-"),
      },
      { title: "Invoice No.", dataIndex: "invoiceNumber", key: "invoiceNumber", ...getSortableColumnProps("invoiceNumber"), width: 110 },
      {
        title: "Payment received",
        dataIndex: "paymentReceived",
        key: "paymentReceived",
        ...getSortableColumnProps("paymentReceived"),
        width: 110,
        render: (v: boolean) => (v ? "Yes" : "No"),
      },
      {
        title: "Payment Receipt date",
        dataIndex: "paymentReceivedDate",
        key: "paymentReceivedDate",
        ...getSortableColumnProps("paymentReceivedDate"),
        width: 130,
        render: (v: string) => (v ? moment(v).format("DD-MMM-YYYY") : "-"),
      },
      {
        title: "Actions",
        key: "actions",
        width: 100,
        fixed: "right" as const,
        render: (_: any, record: any) => (
          <Space>
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewActivityLog(record)}
              title="Activity Log"
            />
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              title="Edit"
            />
          </Space>
        ),
      },
    ],
    [sorter]
  );

 const exportMilestoneList = async () => {
  try {
    const filter: any = {};

    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && filters[key] !== "" && filters[key] !== null) {
        filter[key] = filters[key];
      }
    });

    const exportLimit = Math.max(pagination?.total ?? 0, pageSize, 1000);
    const result = await Client.post(API_MILESTONES_LIST, {
      ...filter,
      page: 1,
      limit: exportLimit,
      sort_by: sorter.sort_by || "order",
      sort_type: sorter.sort_type || "ASC",
    });

    const rows = result?.data?.data || [];

    if (!rows.length) {
      showNotification("No milestone records found", "warning");
      return;
    }

    const rowData = rows.map((item: any) => ({
       Client: item?.accountName || "",
      "SoW#": item?.sowNumber || "",
      "PO Number": item?.poNumber || "",
      Project: item?.projectName || "",
      Spoc: item?.spocName || "",        
      Milestone: item?.name || "",
      Age : item?.percentage || "",     
      "PO Currency": item?.poCurrencyName || "",
      "PO Value": item?.poValue || "",
      "Milestone Value":  item?.percentage != null && item?.poValue != null
        ? formatCost1(
            (Number(item.poValue) * Number(item.percentage)) / 100,
            item?.poCurrencyName || ""
          )
        : "",
      "Estimated Billing Date": item?.estimatedBillingDate
        ? moment(item.estimatedBillingDate).format("DD-MMM-YYYY")
        : "",
      "Invoice Status": item?.invoiceStatus || "",
      "Invoice Date": item?.invoiceDate
        ? moment(item.invoiceDate).format("DD-MMM-YYYY")
        : "",
      "Due Date": item?.invoiceDueDate
        ? moment(item.invoiceDueDate).format("DD-MMM-YYYY")
        : "",
      "Invoice No.": item?.invoiceNumber || "",
      "Payment Received": item?.paymentReceived ? "Yes" : "No",
      "Payment Receipt Date": item?.paymentReceivedDate
        ? moment(item.paymentReceivedDate).format("DD-MMM-YYYY")
        : "",
    }));

    const csv = Papa.unparse(rowData);

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "milestones.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  } catch (error) {
    showNotification("Failed to export milestones", "error");
  }
};

  const formProps = {
    form,
    name: "milestones-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "name",
        label: "",
        placeholder: "Client name",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "projectId",
        label: "",
        placeholder: "Project",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        options: projectOptions,
      },
      {
        name: "poNumber",
        label: "",
        placeholder: "PO Number",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "spoc",
        label: "",
        placeholder: "SPOC",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        options: staffOptions,
      },
      {
        name: "invoiceStatus",
        label: "",
        placeholder: "Invoice Status",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        allowClear: true,
        options: INVOICE_STATUS_OPTIONS,
      },
      {
        name: "paymentReceived",
        label: "",
        placeholder: "Payment received",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        allowClear: true,
        options: [
          { value: true, label: "Yes" },
          { value: false, label: "No" },
        ],
      },
    ],
    initialValues: {},
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
        onClick: clearAllFilters,
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
  };

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <FlagOutlined />
              </div>
              <h1 className="app-page-title">Milestones</h1>
            </div>
            <p className="app-page-subtitle">
              View and manage milestones across projects
            </p>
          </Col>
          <Col>
            <Space size="middle" className="app-page-actions">
               {accessAllowed(myPermissions, allPermissions.VIEW_PROJECT) && (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={exportMilestoneList}
                >
                  <span className="font-size-14">
                    Export Milestone List
                  </span>
                </Button>
               )}
              <Button
                shape="circle"
                type="primary"
                icon={<BiFilterAlt />}
                onClick={() => setFilterVisible(!filterVisible)}
                title={filterVisible ? "Hide filters" : "Show filters"}
              />
            </Space>
          </Col>
        </Row>
      </div>
      {filterVisible && (
        <div className="app-filter-bar">
          <div className="app-filter-header">
            <div className="app-filter-title">
              <BiFilterAlt style={{ fontSize: 14 }} />
              Filters
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setFilterVisible(false)}
              title="Close filters"
              aria-label="Close filters"
            />
          </div>
          <div className="table-filter-form">
            <GenericFormComponent {...formProps} />
          </div>
        </div>
      )}

      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      <div className="app-table-card milestones-table-card">
        <DataTableComponent
          pagination
          loading={loadingMilestonesList}
          dataSource={listData}
          columns={withMilestoneNoWrapColumns(columns)}
          onChange={onChangeHandler}
          defaultPage={0}
          currentPage={pagination?.page ?? 1}
          totalRows={pagination?.total ?? 0}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSize={pageSize}
          pageSizeOptions={["10", "15", "25", "50", "100"]}
          tableWidth="max-content"
        />
      </div>

      {activityLogVisible && selectedMilestoneForLog && (
        <Drawer
          open={activityLogVisible}
          onClose={() => {
            setActivityLogVisible(false);
            setSelectedMilestoneForLog(null);
          }}
          title={`Activity Log - ${selectedMilestoneForLog.name}`}
          width={480}
        >
          <ActivityLogComponent
            id={selectedMilestoneForLog.milestoneId}
            record={selectedMilestoneForLog}
            module="Milestone"
          />
        </Drawer>
      )}

      {editModalVisible && editingMilestone && (
        <CreateEditMilestoneComponent
          visible={editModalVisible}
          onClose={() => {
            setEditModalVisible(false);
            setEditingMilestone(null);
          }}
          projectId={editingMilestone.projectId}
          selectedMilestone={editingMilestone}
          staffOptions={staffOptions}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default ManageMilestonesPage;
